import { defineModule } from '@/js/utils/helpers';

/**
 * Audience switch for the product page. A radiogroup picks the audience; which
 * copy shows is handled in CSS, keyed on [data-audience] on the wrapper. The
 * state is page-wide: the hero carries its own [data-audience] wrapper with
 * [data-aud] spans, so every wrapper on the page is kept in sync and the hero
 * sentence swaps along with the feature rows.
 */

function getElements() {
  return {
    roots: Array.from(
      document.querySelectorAll<HTMLElement>('[data-audience]'),
    ),
    options: Array.from(
      document.querySelectorAll<HTMLElement>('[data-audience-opt]'),
    ),
    fades: Array.from(
      document.querySelectorAll<HTMLElement>('[data-aud-fade]'),
    ),
  };
}

let teardowns: Array<() => void> = [];

export default defineModule(
  () => {
    const { roots, options, fades } = getElements();
    if (!roots.length || !options.length) return;

    const sync = (key: string) => {
      roots.forEach((root) => root.setAttribute('data-audience', key));

      options.forEach((option) => {
        const isActive = option.getAttribute('data-audience-opt') === key;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-checked', isActive ? 'true' : 'false');
        option.tabIndex = isActive ? 0 : -1;
      });
    };

    const activate = (key: string) => {
      sync(key);

      fades.forEach((element) => {
        element.classList.remove('f2f-fade');
        // Read layout to force a reflow so the animation restarts on every switch.
        element.getBoundingClientRect();
        element.classList.add('f2f-fade');
      });
    };

    options.forEach((option, index) => {
      const onClick = () =>
        activate(option.getAttribute('data-audience-opt') ?? '');
      const onKeydown = (event: KeyboardEvent) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const offset = event.key === 'ArrowRight' ? 1 : options.length - 1;
        const next = options[(index + offset) % options.length];
        if (!next) return;
        next.focus();
        activate(next.getAttribute('data-audience-opt') ?? '');
      };

      option.addEventListener('click', onClick);
      option.addEventListener('keydown', onKeydown);
      teardowns.push(() => {
        option.removeEventListener('click', onClick);
        option.removeEventListener('keydown', onKeydown);
      });
    });

    // Adopt whichever audience the markup rendered with, so the buttons and every
    // wrapper on the page start out agreeing — without replaying the fade on load.
    sync(
      roots[0]?.getAttribute('data-audience') ||
        options[0]?.getAttribute('data-audience-opt') ||
        'primary',
    );
  },
  () => {
    teardowns.forEach((teardown) => teardown());
    teardowns = [];
  },
);
